import React, { useContext, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { logout as performLogout } from "../api/axios";
import ReportsMenu from "./ReportsMenu";
import { Menu, X } from "lucide-react";
import logo from "../assets/logo.png";
import { Link, useNavigate } from "react-router-dom";

const Navbar = () => {
  const { auth, setAuth } = useContext(AuthContext);
  const [logoError, setLogoError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = useNavigate();

  const handleLogout = () => {
    setAuth(null);
    performLogout();
    localStorage.setItem("justLoggedOut", "true");
    navigate("/home");
  };

  return (
    <nav className="flex items-center justify-between px-6 py-4 bg-white shadow-md relative">
      {/* Mobile: Centered Logo + Hamburger Right */}
      <div className="flex items-center justify-between w-full md:hidden relative">
        {/* Logo */}
        <Link
          to="/home"
          className="absolute left-1/2 transform -translate-x-1/2 flex items-center"
        >
          {!logoError ? (
            <img
              src={logo}
              alt="NeoFinance Logo"
              className="h-8 w-auto"
              style={{ transform: "scale(5)", transformOrigin: "center" }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="text-2xl font-bold">
              <span className="text-black">Neo</span>
              <span className="text-[#2ecfe3]">Finance</span>
            </div>
          )}
        </Link>

        {/* Hamburger button */}
        <button
          className="ml-auto p-2 text-gray-700 z-50"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Desktop: Logo left */}
      <div className="hidden md:flex items-center">
        <Link to="/home" className="flex items-center">
          {!logoError ? (
            <img
              src={logo}
              alt="NeoFinance Logo"
              className="h-8 w-auto"
              style={{ transform: "scale(5)", transformOrigin: "left center" }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="text-2xl font-bold">
              <span className="text-black">Neo</span>
              <span className="text-[#2ecfe3]">Finance</span>
            </div>
          )}
        </Link>
      </div>

      {/* Desktop Links */}
      <div className="hidden md:flex items-center space-x-6 text-gray-800 font-medium">
        <Link to="/home" className="hover:text-[#2ecfe3]">Home</Link>
        <Link to="/dashboard" className="hover:text-[#2ecfe3]">Dashboard</Link>
        <Link to="/transactions" className="hover:text-[#2ecfe3]">Transactions</Link>
        <Link to="/assistant" className="hover:text-[#2ecfe3]">Assistant</Link>
        {auth?.isStaff && (
          <Link to="/admin" className="hover:text-[#2ecfe3]">Admin</Link>
        )}

        {/* Reports Dropdown */}
        <div className="relative group">
          <div className="flex items-center cursor-pointer hover:text-[#2ecfe3]">
            <span>Reports</span>
            <svg className="w-4 h-4 ml-1 fill-current" viewBox="0 0 20 20">
              <path d="M5.25 7.75L10 12.5l4.75-4.75" />
            </svg>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-full hidden group-hover:block bg-white border rounded shadow-lg z-50">
            <ReportsMenu />
          </div>
        </div>
      </div>

      {/* Desktop Auth */}
      <div className="hidden md:flex items-center space-x-4 text-sm font-medium text-gray-700">
        {auth ? (
          <>
            <span>{auth.username}</span>
            <span className="text-gray-400">|</span>
            <button
              onClick={handleLogout}
              className="hover:text-[#2ecfe3] focus:outline-none"
            >
              Logout
            </button>
          </>
        ) : (
          <>
            <Link to="/login" className="hover:text-[#2ecfe3]">Login</Link>
            <span className="text-gray-400">|</span>
            <Link to="/register" className="hover:text-[#2ecfe3]">Register</Link>
          </>
        )}
      </div>

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg transform ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out z-40`}
      >
        <div className="flex flex-col p-6 space-y-4 text-gray-800 font-medium">
          <Link to="/home" onClick={() => setMenuOpen(false)}>Home</Link>
          <Link to="/dashboard" onClick={() => setMenuOpen(false)}>Dashboard</Link>
          <Link to="/transactions" onClick={() => setMenuOpen(false)}>Transactions</Link>
          <Link to="/assistant" onClick={() => setMenuOpen(false)}>Assistant</Link>
          {auth?.isStaff && (
            <Link to="/admin" onClick={() => setMenuOpen(false)}>Admin</Link>
          )}

          {/* MOBILE Reports */}
          <div>
            <div className="font-semibold">Reports</div>
            <div className="mt-2">
              <ReportsMenu onNavigate={() => setMenuOpen(false)} />
            </div>
          </div>

          <div className="border-t pt-4">
            {auth ? (
              <button
                onClick={() => {
                  handleLogout();
                  setMenuOpen(false);
                }}
                className="text-red-500"
              >
                Logout
              </button>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
                <span className="mx-2 text-gray-400">|</span>
                <Link to="/register" onClick={() => setMenuOpen(false)}>Register</Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-30"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </nav>
  );
};

export default Navbar;
